import { useCallback, useRef, useState } from 'react';
import dayjs from 'app/libs/dayjs';
import { getSessionItem, removeSessionItem, setSessionItem } from 'app/libs/storageUtils';
import { WITHDRAW_CANCEL_SESSION_EXPIRE_MINUTES } from 'app/constants/withdraw';

const WITHDRAW_CANCEL_DATA_KEY = 'withdraw_cancel_data';

/** 취소 화면을 영구적으로 떠나는 지점(닫기 버튼·조회 실패·로그아웃 등)에서 훅 밖에서도 호출 가능하도록 분리 */
export const clearWithdrawCancelSession = () => {
	removeSessionItem(WITHDRAW_CANCEL_DATA_KEY);
};

/**
 * 탈퇴 신청 취소 화면의 입력 상태를 sessionStorage에 임시 보관하는 훅.
 *
 * 약관 링크(이용약관/클린엔카 규정)가 target="_self"라 같은 탭에서 서버 렌더 페이지로 이탈했다가
 * 돌아오면 앱이 재부팅되어 React state가 전부 초기화되기 때문에, 왕복 동안만 상태를 살려둔다.
 *
 * 보관 항목에 비밀번호(평문)가 포함되므로 노출 창을 최소화한다.
 * - sessionStorage 사용(탭 종료 시 소멸). 쿠키는 매 요청 헤더로 서버에 전송되므로 쓰지 않는다.
 * - 진입 링크 토큰(`?c=`)으로 스코핑해 다른 회원의 링크로 재진입하면 폐기한다.
 * - 최초 저장 시점부터 30분 만료(슬라이딩 아님). 취소 완료 시 `clearSession()`으로 즉시 삭제한다.
 */
function useWithdrawCancelSession(token) {
	const [session, setSession] = useState(() => {
		const stored = getSessionItem(WITHDRAW_CANCEL_DATA_KEY);
		if (!stored) return {};

		const isSameUser = Boolean(token) && stored.token === token;
		const isExpired = !stored.expiredAt || dayjs().isAfter(stored.expiredAt);
		if (isSameUser && !isExpired) return stored;

		clearWithdrawCancelSession();
		return {};
	});

	// 스토리지 쓰기는 setState 업데이터 밖에서 수행하기 위해 최신 값을 ref로 들고 간다.
	const sessionRef = useRef(session);

	const updateSession = useCallback(
		(patch) => {
			const prev = sessionRef.current;
			const next = {
				...prev,
				...patch,
				token,
				// 만료 시각은 최초 저장 시점에만 찍고 이후 갱신하지 않는다.
				expiredAt: prev.expiredAt || dayjs().add(WITHDRAW_CANCEL_SESSION_EXPIRE_MINUTES, 'minutes').toISOString(),
			};

			sessionRef.current = next;
			setSessionItem(WITHDRAW_CANCEL_DATA_KEY, next);
			setSession(next);
		},
		[token],
	);

	const clearSession = useCallback(() => {
		sessionRef.current = {};
		clearWithdrawCancelSession();
		setSession({});
	}, []);

	return { session, updateSession, clearSession };
}

export default useWithdrawCancelSession;
